import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";

import { Button } from "@/components/Button";

export interface ParsedErrorLocation {
    caller: string;
    file: string;
    line: string;
    column: string;
    fullPath: string;
}

export const parseStackTrace = (stack?: string): ParsedErrorLocation | null => {
    if (!stack || typeof stack !== "string") {
        return null;
    }

    const lines = stack.split("\n");
    for (const line of lines) {
        const match = line.match(
            /(?:at\s+(?:([^\s(]+)\s+\()?)?(?:https?:\/\/[^/]+)?([^?)\s:]+)(?:\?[^):]*)?:(\d+):(\d+)\)?/
        );
        if (match && match[2]) {
            const path = match[2];
            if (path.includes("src/")) {
                return {
                    caller: match[1] || "",
                    file: path.substring(path.indexOf("src/")),
                    line: match[3],
                    column: match[4],
                    fullPath: path,
                };
            }
        }
    }

    for (const line of lines) {
        const match = line.match(
            /(?:at\s+(?:([^\s(]+)\s+\()?)?(?:https?:\/\/[^/]+)?([^?)\s:]+)(?:\?[^):]*)?:(\d+):(\d+)\)?/
        );
        if (
            match &&
            match[2] &&
            !match[2].includes("node_modules") &&
            !match[2].includes("/@vite/")
        ) {
            return {
                caller: match[1] || "",
                file: match[2],
                line: match[3],
                column: match[4],
                fullPath: match[2],
            };
        }
    }

    return null;
};

export interface ErrorPageProps {
    error?: Error | null | unknown;
    resetErrorBoundary?: () => void;
}

export const Error: React.FC<ErrorPageProps> = ({
    error,
    resetErrorBoundary,
}) => {
    const { t } = useTranslation("global");
    const [copiedMessage, setCopiedMessage] = useState<boolean>(false);
    const [copiedDetails, setCopiedDetails] = useState<boolean>(false);
    const [copiedStack, setCopiedStack] = useState<boolean>(false);
    const [showStack, setShowStack] = useState<boolean>(true);

    const errorObj =
        error && typeof error === "object"
            ? (error as Record<string, unknown>)
            : null;
    const errorMessage =
        (errorObj?.message as string) ||
        (typeof error === "string" ? error : JSON.stringify(error));
    const errorName = (errorObj?.name as string) || "Error";
    const errorStack = (errorObj?.stack as string) || "";
    const location = parseStackTrace(errorStack);
    const publicError = import.meta.env.PROD;
    const visibleMessage = publicError
        ? t("error.public_message")
        : errorMessage;

    const handleReload = (): void => {
        if (resetErrorBoundary) {
            resetErrorBoundary();
        } else {
            window.location.reload();
        }
    };

    const handleCopyMessage = async (): Promise<void> => {
        try {
            await navigator.clipboard.writeText(errorMessage);
            setCopiedMessage(true);
            setTimeout(() => {
                setCopiedMessage(false);
            }, 2000);
        } catch {
            // clipboard fallback if any
        }
    };

    const handleCopyDetails = async (): Promise<void> => {
        const lines: string[] = [`${t("error.message")}: ${errorMessage}`];

        if (location?.file) {
            lines.push(`${t("error.file")}: ${location.file}`);
        }

        if (location?.line) {
            lines.push(`${t("error.line")}: ${location.line}`);
        }

        if (location?.caller) {
            lines.push(`${t("error.caller")}: ${location.caller}`);
        }

        try {
            await navigator.clipboard.writeText(lines.join("\n"));
            setCopiedDetails(true);
            setTimeout(() => {
                setCopiedDetails(false);
            }, 2000);
        } catch {
            // clipboard fallback if any
        }
    };

    const handleCopyStack = async (): Promise<void> => {
        const textToCopy = `${errorName}: ${errorMessage}\n\n${errorStack}`;
        try {
            await navigator.clipboard.writeText(textToCopy);
            setCopiedStack(true);
            setTimeout(() => {
                setCopiedStack(false);
            }, 2000);
        } catch {
            // clipboard fallback if any
        }
    };

    return (
        <div className="min-h-screen bg-page flex items-center justify-center p-4 sm:p-6 font-sans">
            <div className="max-w-2xl w-full bg-white dark:bg-dark-box rounded-2xl border border-slate-200 dark:border-white/10 shadow-2xl p-6 sm:p-8 flex flex-col items-center text-center space-y-6">
                {/* Header: Centered Icon on top + Title underneath */}
                <div className="flex flex-col items-center justify-center space-y-3">
                    <div className="w-16 h-16 rounded-2xl bg-rose-50 dark:bg-rose-950/50 text-rose-600 dark:text-rose-400 flex items-center justify-center shadow-2xs border border-rose-100 dark:border-rose-900/40">
                        <Icon value="alertTriangle" size={32} />
                    </div>

                    <div className="space-y-1">
                        <h2 className="text-xl font-bold text-slate-900 dark:text-white leading-tight">
                            {t("error.title")}
                        </h2>
                        {!publicError && errorName && (
                            <span className="text-xs font-mono text-rose-600 dark:text-rose-400 inline-block">
                                {errorName}
                            </span>
                        )}
                    </div>
                </div>

                {/* Error Message Box */}
                <button
                    type="button"
                    onClick={() => {
                        void handleCopyMessage();
                    }}
                    title={
                        copiedMessage
                            ? t("error.copied")
                            : t("error.copy_message")
                    }
                    className="group/error w-full max-w-xl p-3.5 bg-rose-50/70 dark:bg-rose-950/30 border border-rose-200/80 dark:border-rose-900/50 rounded-xl text-xs font-mono text-rose-800 dark:text-rose-300 break-words leading-relaxed text-center cursor-pointer transition-colors hover:bg-rose-100/80 dark:hover:bg-rose-950/45 flex items-start justify-center gap-2"
                >
                    <span className="min-w-0 flex-1">{visibleMessage}</span>
                    <Icon
                        value={copiedMessage ? "check" : "copy"}
                        size={14}
                        className={`shrink-0 mt-0.5 opacity-70 group-hover/error:opacity-100 ${copiedMessage ? "text-emerald-600 dark:text-emerald-400" : ""}`}
                    />
                </button>

                {/* Detected File and Line Info */}
                {!publicError && location && (
                    <div className="w-full max-w-xl overflow-hidden rounded-xl border border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-black/30 text-xs font-mono text-left">
                        <div
                            onClick={() => {
                                void handleCopyDetails();
                            }}
                            title={
                                copiedDetails
                                    ? t("error.copied")
                                    : t("error.copy_details")
                            }
                            className="flex items-center justify-between border-b border-slate-200 px-3.5 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:border-white/10 dark:text-zinc-400 font-sans cursor-pointer hover:bg-slate-100/70 dark:hover:bg-white/5 transition-colors group/details select-none"
                        >
                            <span className="w-full text-center">
                                {t("error.details")}
                            </span>
                            <Icon
                                value={copiedDetails ? "check" : "copy"}
                                size={14}
                                className={`shrink-0 transition-colors ${copiedDetails ? "text-emerald-600 dark:text-emerald-400" : "text-slate-400 group-hover/details:text-slate-700 dark:text-zinc-400 dark:group-hover/details:text-zinc-200"}`}
                            />
                        </div>
                        <div className="border-b border-slate-200 px-3.5 py-2.5 dark:border-white/10">
                            <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-zinc-400 font-sans">
                                {t("error.message")}
                            </span>
                            <span className="mt-1 block break-all font-semibold text-primary dark:text-primary-400">
                                {errorMessage}
                            </span>
                        </div>
                        <div className="border-b border-slate-200 px-3.5 py-2.5 dark:border-white/10">
                            <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-zinc-400 font-sans">
                                {t("error.file")}
                            </span>
                            <span className="mt-1 block break-all font-semibold text-primary dark:text-primary-400">
                                {location.file}
                            </span>
                        </div>
                        <div
                            className={`px-3.5 py-2.5 ${location.caller ? "border-b border-slate-200 dark:border-white/10" : ""}`}
                        >
                            <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-zinc-400 font-sans">
                                {t("error.line")}
                            </span>
                            <span className="mt-1 block font-semibold text-amber-700 dark:text-amber-300">
                                {location.line}
                            </span>
                        </div>
                        {location.caller && (
                            <div className="px-3.5 py-2.5">
                                <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-zinc-400 font-sans">
                                    {t("error.caller")}
                                </span>
                                <span className="mt-1 block break-all text-slate-600 dark:text-zinc-400">
                                    {location.caller}
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {/* Stack Trace Accordion */}
                {!publicError && errorStack && (
                    <div className="w-full max-w-xl space-y-2">
                        <div className="flex items-center justify-between px-1">
                            <button
                                type="button"
                                onClick={() => setShowStack((prev) => !prev)}
                                className="text-xs font-semibold text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white flex items-center space-x-1 cursor-pointer transition-colors"
                            >
                                <Icon
                                    value={
                                        showStack
                                            ? "chevronDown"
                                            : "chevronRight"
                                    }
                                    size={14}
                                    className="transition-transform"
                                />
                                <span>{t("error.stack")}</span>
                            </button>

                            <button
                                type="button"
                                onClick={handleCopyStack}
                                className="text-[11px] font-medium text-slate-500 dark:text-zinc-400 hover:text-primary flex items-center space-x-1 cursor-pointer transition-colors"
                            >
                                <Icon
                                    value={copiedStack ? "check" : "copy"}
                                    size={13}
                                    className={
                                        copiedStack ? "text-emerald-600" : ""
                                    }
                                />
                                <span>
                                    {copiedStack
                                        ? t("error.copied")
                                        : t("error.copy_stack")}
                                </span>
                            </button>
                        </div>

                        {showStack && (
                            <pre className="p-3.5 bg-slate-900 text-slate-200 dark:bg-black dark:text-zinc-300 rounded-xl text-[11px] font-mono leading-relaxed overflow-x-auto max-h-56 overflow-y-auto border border-slate-800 dark:border-white/10 select-text text-left">
                                {errorStack}
                            </pre>
                        )}
                    </div>
                )}

                {/* Action Button: Centered without any border between code and button */}
                <div className="flex justify-center pt-2">
                    <Button
                        type="button"
                        onClick={handleReload}
                        variant="primary"
                        className="flex items-center gap-2 px-6 py-2.5 rounded-xl shadow-xs cursor-pointer"
                        icon={<Icon value="reload" size={15} />}
                        text={t("error.reload")}
                    />
                </div>
            </div>
        </div>
    );
};

export default Error;
